import { useCallback, useEffect, useRef } from 'react';
import { AudioPosition } from '@/types/audioPosition';
import { saveAudioProgress, clearAudioProgress } from '@/utils/audioProgressUtils';

interface UseAudioPositionProps {
  audioUrl: string;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isReady: boolean;
  duration: number;
  bookName: string;
  chapterId?: string;
  chapterStartTime?: number;
}

// Saved-position bookkeeping only. Applying a saved/target offset to the
// audio element and deciding when it's safe to resume playback is owned by
// useAudioManager's PlaybackIntent resolution, which coordinates with reader
// content/position readiness — this hook no longer restores anything on its
// own.
export const useAudioPosition = ({ audioUrl, audioRef, isReady, duration, bookName, chapterId, chapterStartTime = 0 }: UseAudioPositionProps) => {
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPositionRestored = isReady && duration > 0;

  // Save audio position to cookie (debounced)
  const savePosition = useCallback((position: AudioPosition) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (position.timeOffset > 0) {
        const positionWithTimestamp = {
          ...position,
          lastPlayed: Date.now(),
          chapterId: chapterId,
          totalProgress: chapterStartTime + position.timeOffset
        };
        saveAudioProgress(bookName, positionWithTimestamp);
        console.log('Saved audio position:', positionWithTimestamp);
      }
    }, 2000);
  }, [bookName, chapterId, chapterStartTime]);

  // Clear saved position
  const clearSavedPosition = useCallback(() => {
    clearAudioProgress(bookName);
    console.log('Cleared saved audio position');
  }, [bookName]);

  // Save immediately with full position
  const savePositionNow = useCallback((position: AudioPosition) => {
    if (position.timeOffset > 0) {
      const positionWithTimestamp = {
        ...position,
        lastPlayed: Date.now(),
        chapterId: chapterId,
        totalProgress: chapterStartTime + position.timeOffset
      };
      saveAudioProgress(bookName, positionWithTimestamp);
    }
  }, [bookName, chapterId, chapterStartTime]);

  // Save position when page is about to unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      const time = audioRef.current?.currentTime || 0;
      if (time > 0) {
        const position: AudioPosition = {
            sentenceIndex: -1,
            textFingerprint: '',
            timeOffset: time,
            lastPlayed: Date.now(),
            chapterId,
            totalProgress: chapterStartTime + time
          };
        saveAudioProgress(bookName, position);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [bookName, audioRef, chapterId, audioUrl, chapterStartTime]);

  // Cleanup timeout on unmount or when book changes
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [bookName]);

  return {
    savePosition,
    savePositionNow,
    clearSavedPosition,
    isPositionRestored,
  };
};
